import { useState, useEffect, Fragment, useCallback, useRef } from "react";
import {
  ResponseSuccessModal,
  ResponseFailModal,
} from "../../components/Modal/Response";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../stores";
import {
  changeField,
  changeResult,
  createTermTemplatesAction,
  initialize,
} from "../../stores/termsTemplates";
import { TermTemplatesCreateContainerProps } from "../../types/termTemplates";
import TermTemplatesCreate from "../../components/TermsTemplates/TermTemplatesCreate";
import ReactQuill from "react-quill";

const TermTemplatesCreateContainer = ({
  isOpen,
  handleViewChange,
  listTermTemplates,
}: TermTemplatesCreateContainerProps) => {
  const editorRef = useRef<ReactQuill>(null);
  const dispatch = useDispatch();

  const {
    termTemplatesItem,
    termTemplatesCreateSuccess,
    termTemplatesCreateError,
  } = useSelector(({ termTemplates }: RootState) => ({
    termTemplatesItem: termTemplates.termTemplates,
    termTemplatesCreateSuccess: termTemplates.termTemplatesCreateSuccess,
    termTemplatesCreateError: termTemplates.termTemplatesCreateError,
  }));

  // 모달 상태 관리 (결과 알림용)
  const [
    isOpenTermTemplatesCreateSuccessModal,
    setIsOpenTermTemplatesCreateSuccessModal,
  ] = useState(false);
  const [
    isOpenTermTemplatesCreateFailModal,
    setIsOpenTermTemplatesCreateFailModal,
  ] = useState(false);
  const [
    isOpenRequiredParameterFailModal,
    setIsOpenRequiredParameterFailModal,
  ] = useState(false);
  const [messageTermTemplatesCreateFail, setMessageTermTemplatesCreateFail] =
    useState("");

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | string) => {
      if (typeof e === "string") {
        dispatch(changeField({ key: "content", value: e }));
      } else {
        const { value, name } = e.target;
        dispatch(changeField({ key: name, value }));
      }
    },
    [dispatch],
  );

  const handleCreateTermTempates = useCallback(() => {
    if (
      !termTemplatesItem.title ||
      !termTemplatesItem.content ||
      !termTemplatesItem.language
    ) {
      setIsOpenRequiredParameterFailModal(true);
      return;
    }
    dispatch(
      createTermTemplatesAction({ ...termTemplatesItem, is_published: false }),
    );
  }, [termTemplatesItem, dispatch]);

  // 디테일 페이지와 동일한 뒤로가기 로직
  const handleGoBack = useCallback(() => {
    // 리스트를 새로고침하고 뷰를 리스트로 전환
    listTermTemplates();
    handleViewChange("termsTemplates", "list");

    // 에러 상태 초기화 (필요시)
    dispatch(
      changeResult({
        key: "termTemplatesCreateError",
        value: null,
      }),
    );
  }, [listTermTemplates, handleViewChange, dispatch]);

  useEffect(() => {
    if (isOpen) {
      dispatch(initialize()); // 등록 페이지 진입 시 데이터 초기화
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (termTemplatesCreateError) {
      const errorMsg =
        termTemplatesCreateError.response?.data?.message ||
        termTemplatesCreateError.message;
      setMessageTermTemplatesCreateFail(errorMsg);
      setIsOpenTermTemplatesCreateFailModal(true);

      dispatch(changeResult({ key: "termTemplatesCreateError", value: null }));
    }

    if (termTemplatesCreateSuccess) {
      setIsOpenTermTemplatesCreateSuccessModal(true);
      setTimeout(() => {
        setIsOpenTermTemplatesCreateSuccessModal(false);
        dispatch(
          changeResult({ key: "termTemplatesCreateSuccess", value: false }),
        );
        handleGoBack(); // 등록 성공 후 리스트로 이동
      }, 1500);
    }
  }, [
    termTemplatesCreateSuccess,
    termTemplatesCreateError,
    dispatch,
    handleGoBack,
  ]);

  return (
    <Fragment>
      {/* 부트스트랩 Modal 태그를 제거하고 컴포넌트만 렌더링 */}
      <TermTemplatesCreate
        termTemplatesItem={termTemplatesItem}
        editorRef={editorRef}
        handleChange={handleChange}
        handleCreateTermTempates={handleCreateTermTempates}
        handleGoBack={handleGoBack}
      />

      {/* 결과 알림 모달들은 유지 */}
      <ResponseSuccessModal
        isOpen={isOpenTermTemplatesCreateSuccessModal}
        toggle={() =>
          setIsOpenTermTemplatesCreateSuccessModal(
            !isOpenTermTemplatesCreateSuccessModal,
          )
        }
        message="약관 등록이 완료 되었습니다."
      />
      <ResponseFailModal
        isOpen={isOpenTermTemplatesCreateFailModal}
        toggle={() =>
          setIsOpenTermTemplatesCreateFailModal(
            !isOpenTermTemplatesCreateFailModal,
          )
        }
        message={
          messageTermTemplatesCreateFail || "약관 등록에 실패 하였습니다."
        }
      />
      <ResponseFailModal
        isOpen={isOpenRequiredParameterFailModal}
        toggle={() =>
          setIsOpenRequiredParameterFailModal(!isOpenRequiredParameterFailModal)
        }
        message="필수 입력값을 확인 해주세요."
      />
    </Fragment>
  );
};

export default TermTemplatesCreateContainer;
